import { daysSinceWeekStart, type WeekStartDay } from "~/shared/kpi"

/**
 * Cohort-window presets for the LTV Cohorts report. All math is UTC ISO-string
 * based (no dayjs — its locale week start is Sunday while reporting weeks
 * follow the company's Financial Settings). Presets only ever cover COMPLETE
 * periods: monthly windows end at the last complete month, weekly windows at
 * the last complete reporting week, and calendar presets are clamped the same
 * way (a preset that would be empty after clamping is omitted).
 *
 * Calendar presets pass raw calendar boundaries as the cohort dateRange; in
 * weekly view the boundary weeks are therefore PARTIAL cohorts (e.g. the week
 * straddling Jan 1 only counts members from Jan 1 on), matching how every
 * other weekly report treats calendar ranges.
 */

export type PeriodPreset = {
  id: string
  label: string
  start: string
  end: string
}

export const DEFAULT_MONTHLY_PRESET_ID = "last-12-months"
export const DEFAULT_WEEKLY_PRESET_ID = "last-26-weeks"
export const CUSTOM_PRESET_ID = "custom"

const DAY_MS = 86_400_000

function utcMs(iso: string): number {
  return Date.UTC(
    Number(iso.slice(0, 4)),
    Number(iso.slice(5, 7)) - 1,
    Number(iso.slice(8, 10)),
  )
}

function isoFromUtcMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDaysIso(iso: string, days: number): string {
  return isoFromUtcMs(utcMs(iso) + days * DAY_MS)
}

function startOfMonthIso(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

function addMonthsToMonthStart(monthStartIso: string, months: number): string {
  const year = Number(monthStartIso.slice(0, 4))
  const monthIndex = Number(monthStartIso.slice(5, 7)) - 1 + months
  return isoFromUtcMs(Date.UTC(year, monthIndex, 1))
}

/** End of the most recent COMPLETE month (yesterday's month may be current). */
export function lastCompleteMonthEnd(todayIso: string): string {
  return addDaysIso(startOfMonthIso(todayIso), -1)
}

/** Start of the current (incomplete) reporting week. */
export function currentWeekStart(
  todayIso: string,
  weekStartDay: WeekStartDay,
): string {
  const dayOfWeek = new Date(utcMs(todayIso)).getUTCDay()
  return addDaysIso(todayIso, -daysSinceWeekStart(dayOfWeek, weekStartDay))
}

function clampPreset(
  id: string,
  label: string,
  start: string,
  rawEnd: string,
  completeEnd: string,
): PeriodPreset | null {
  const end = rawEnd < completeEnd ? rawEnd : completeEnd
  if (end < start) return null
  return { id, label, start, end }
}

export function monthlyPresets(todayIso: string): PeriodPreset[] {
  const currentMonthStart = startOfMonthIso(todayIso)
  const end = lastCompleteMonthEnd(todayIso)
  const currentYear = Number(todayIso.slice(0, 4))

  const presets: PeriodPreset[] = [
    {
      id: DEFAULT_MONTHLY_PRESET_ID,
      label: "Last 12 months",
      start: addMonthsToMonthStart(currentMonthStart, -12),
      end,
    },
    {
      id: "last-6-months",
      label: "Last 6 months",
      start: addMonthsToMonthStart(currentMonthStart, -6),
      end,
    },
  ]

  for (let year = currentYear; year >= currentYear - 2; year -= 1) {
    const preset = clampPreset(
      `year-${year}`,
      `${year}`,
      `${year}-01-01`,
      `${year}-12-31`,
      end,
    )
    if (preset) presets.push(preset)
  }

  return presets
}

export function weeklyPresets(
  todayIso: string,
  weekStartDay: WeekStartDay,
): PeriodPreset[] {
  const weekStart = currentWeekStart(todayIso, weekStartDay)
  const end = addDaysIso(weekStart, -1)
  const currentYear = Number(todayIso.slice(0, 4))

  const presets: PeriodPreset[] = [
    {
      id: DEFAULT_WEEKLY_PRESET_ID,
      label: "Last 26 weeks",
      start: addDaysIso(weekStart, -26 * 7),
      end,
    },
    {
      id: "last-13-weeks",
      label: "Last 13 weeks",
      start: addDaysIso(weekStart, -13 * 7),
      end,
    },
    {
      id: "last-52-weeks",
      label: "Last 52 weeks",
      start: addDaysIso(weekStart, -52 * 7),
      end,
    },
  ]

  for (let year = currentYear; year >= currentYear - 1; year -= 1) {
    const yearPreset = clampPreset(
      `year-${year}`,
      `${year}`,
      `${year}-01-01`,
      `${year}-12-31`,
      end,
    )
    if (yearPreset) presets.push(yearPreset)

    const halves: Array<[string, string, string]> = [
      [`h1-${year}`, `${year}-01-01`, `${year}-06-30`],
      [`h2-${year}`, `${year}-07-01`, `${year}-12-31`],
    ]
    halves.forEach(([id, start, rawEnd], index) => {
      const preset = clampPreset(
        id,
        `H${index + 1} ${year}`,
        start,
        rawEnd,
        end,
      )
      if (preset) presets.push(preset)
    })
  }

  // Last 6 quarters, most recent (possibly in-progress, clamped) first.
  const currentQuarterIndex = Math.floor((Number(todayIso.slice(5, 7)) - 1) / 3)
  for (let back = 0; back < 6; back += 1) {
    const absoluteQuarter = currentYear * 4 + currentQuarterIndex - back
    const year = Math.floor(absoluteQuarter / 4)
    const quarter = absoluteQuarter % 4
    const startMonth = quarter * 3 + 1
    const start = `${year}-${String(startMonth).padStart(2, "0")}-01`
    const rawEnd = addDaysIso(
      isoFromUtcMs(Date.UTC(year, startMonth + 2, 1)),
      -1,
    )
    const preset = clampPreset(
      `q${quarter + 1}-${year}`,
      `Q${quarter + 1} ${year}`,
      start,
      rawEnd,
      end,
    )
    if (preset) presets.push(preset)
  }

  return presets
}
